import { createRouter, createWebHistory, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import HomeView from '../views/HomeView.vue'

// Simple loading component for OAuth callback handling
const LoginCallbackHandler = {
  template: '<div style="display: flex; justify-content: center; align-items: center; height: 100vh;"><div>Processing login...</div></div>',
  async setup() {
    const authStore = useAuthStore()
    const route = useRoute()
    const router = useRouter()
    
    // If this is an OAuth success callback, validate session and redirect
    if (route.query.success === 'true' && route.query.redirect) {
      const redirectPath = decodeURIComponent(route.query.redirect as string)
      const hasSession = await authStore.checkSession()
      
      if (hasSession && authStore.isAuthenticated) {
        // Session is valid, navigate to target
        router.push(redirectPath)
      } else {
        // Session not ready, redirect to home
        router.push('/')
      }
    } else {
      // No success param, go home
      router.push('/')
    }
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/login',
      name: 'login',
      component: LoginCallbackHandler
    },
    {
      path: '/creator',
      component: () => import('../views/creator/CreatorLayout.vue'),
      meta: { requiresAuth: true, requiresCreator: true },
      children: [
        {
          path: '',
          name: 'creator-studio',
          component: () => import('../views/creator/CreatorDashboard.vue')
        },
        {
          path: 'content',
          name: 'creator-content-library',
          component: () => import('../views/creator/ContentLibrary.vue')
        },
        {
          path: 'content/:id',
          name: 'creator-content-view',
          component: () => import('../views/creator/ContentView.vue')
        },
        {
          path: 'content/:id/edit',
          name: 'creator-content-edit',
          component: () => import('../views/creator/EditContent.vue')
        },
        {
          path: 'content/:id/preview',
          name: 'creator-content-preview',
          component: () => import('../views/creator/BookPreview.vue')
        },
        {
          path: 'content/:id/workbench',
          name: 'creator-content-workbench',
          component: () => import('../views/creator/BookWorkbench.vue')
        },
        {
          path: 'vocabulary',
          name: 'creator-vocabulary',
          component: () => import('../views/creator/CreatorVocabulary.vue')
        }
      ]
    },
    {
      path: '/guardian',
      component: () => import('../views/guardian/GuardianLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'guardian',
          redirect: '/guardian/store'
        },
        {
          path: 'store',
          name: 'guardian-store',
          component: () => import('../views/guardian/StoreView.vue')
        },
        {
          path: 'library',
          name: 'guardian-library',
          component: () => import('../views/guardian/LibraryView.vue')
        },
        {
          path: 'backpack',
          name: 'guardian-backpack',
          component: () => import('../views/guardian/BackpackView.vue')
        }
      ]
    },
    {
      path: '/learner',
      component: () => import('../views/learner/LearnerLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'learner-backpack',
          component: () => import('../views/learner/LearnerBackpack.vue')
        },
        {
          path: 'content/:id',
          name: 'learner-content',
          component: () => import('../views/learner/ContentView.vue')
        }
      ]
    },
    {
      path: '/reviewer',
      component: () => import('../views/reviewer/ReviewerLayout.vue'),
      meta: { requiresAuth: true, requiresReviewer: true },
      children: [
        {
          path: '',
          name: 'reviewer-queue',
          component: () => import('../views/reviewer/ReviewQueue.vue')
        },
        {
          path: 'review/:id',
          name: 'reviewer-review',
          component: () => import('../views/reviewer/ReviewContent.vue')
        }
      ]
    },
    {
      path: '/admin',
      component: () => import('../views/admin/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: () => import('../views/admin/AdminDashboard.vue')
        },
        {
          path: 'content',
          name: 'admin-content',
          component: () => import('../views/admin/AdminContent.vue')
        },
        {
          path: 'prompts',
          name: 'admin-prompts',
          component: () => import('../views/admin/AdminPrompts.vue')
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('../views/admin/AdminUsers.vue')
        }
      ]
    }
  ]
})

// Navigation guards - auto-redirect to IdYangu if no session
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Check session
    const hasSession = await authStore.checkSession()

    if (!hasSession || !authStore.isAuthenticated) {
      // No session – redirect to API so backend responds with 302 to IdYangu (same as schoolbus)
      window.location.href = `/api/auth/idyangu/login?redirect=${encodeURIComponent(to.fullPath)}`
      return
    }

    // Check role requirements
    if (to.meta.requiresCreator && !authStore.user?.is_creator) {
      // Send non-creators to guardian dashboard to avoid redirect loops
      next('/guardian')
      return
    }

    if (to.meta.requiresReviewer && !authStore.user?.is_reviewer) {
      window.location.href = `/api/auth/idyangu/login?redirect=${encodeURIComponent(to.fullPath)}`
      return
    }

    if (to.meta.requiresAdmin && authStore.user?.role !== 'admin') {
      next('/guardian')
      return
    }
  }

  // For home page, always show landing page (no auto-redirect)
  // If authenticated, user can navigate to their dashboard via buttons
  if (to.path === '/') {
    // Optionally check session to show different content, but don't redirect
    await authStore.checkSession()
    next()
    return
  }
  
  next()
})

export default router

